import React, { useCallback, useEffect, useRef, useState } from "react";
import type {
  DraftPost,
  MetaCarouselSlide,
  MetaContentFormat,
} from "../types";
import {
  CAROUSEL_LIMITS,
  CarouselPlatformKey,
  getCarouselLimits,
} from "../utils/instagramCarouselLimits";
import CarouselSlideStrip from "../../../Shared/CarouselSlideStrip";

type FormatOption = {
  value: MetaContentFormat;
  label: string;
  icon: string;
};

const INSTAGRAM_FORMAT_OPTIONS: FormatOption[] = [
  { value: "carousel", label: "Carousel", icon: "view_carousel" },
  { value: "image", label: "Image", icon: "image" },
  { value: "video", label: "Video", icon: "movie" },
];

const FACEBOOK_FORMAT_OPTIONS: FormatOption[] = [
  { value: "carousel", label: "Carousel", icon: "view_carousel" },
  { value: "image", label: "Image", icon: "image" },
  { value: "video", label: "Video", icon: "movie" },
];

function getFormatOptions(platform: CarouselPlatformKey): FormatOption[] {
  return platform === "FACEBOOK" ? FACEBOOK_FORMAT_OPTIONS : INSTAGRAM_FORMAT_OPTIONS;
}

function FormatIcon({ name, className }: { name: string; className?: string }) {
  return <span className={`material-icons ${className ?? ""}`.trim()}>{name}</span>;
}

/** Canvas format from a draft, reading the new field first then the legacy IG-named field. */
export function getContentFormat(post: DraftPost): MetaContentFormat {
  if (post.content_format) return post.content_format;
  if (post.instagram_content_format) return post.instagram_content_format;
  if (post.carousel_slides && post.carousel_slides.length > 0) return "carousel";
  if (post.video_url) return "video";
  return "image";
}

/** @deprecated Use getContentFormat. */
export const getInstagramFormat = getContentFormat;

function reorderSlides(
  slides: MetaCarouselSlide[],
  from: number,
  to: number
): MetaCarouselSlide[] {
  if (from === to || from < 0 || to < 0 || from >= slides.length || to >= slides.length) {
    return slides;
  }
  const next = [...slides];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export interface MetaCanvasPreviewProps {
  post: DraftPost;
  onUpdate: (next: DraftPost) => void;
  /** Defaults to INSTAGRAM for back-compat with prior IG-only usage. */
  editorPlatform?: CarouselPlatformKey;
  uploadMedia?: {
    onLibrary: () => void;
    onLocalFile: () => void;
    onLibraryCarousel?: () => void;
  };
  mediaUploadRef?: React.RefObject<HTMLDivElement | null>;
  carouselAddRef?: React.RefObject<HTMLDivElement | null>;
  renderUploadMenuPopover: (placement: "media" | "carouselAdd") => React.ReactNode;
  toggleUploadMenu: (placement: "media" | "carouselAdd") => void;
  onRemoveHeroMedia?: () => void;
}

/** @deprecated Use MetaCanvasPreviewProps. */
export type InstagramCanvasPreviewProps = MetaCanvasPreviewProps;

export const MetaCanvasPreview: React.FC<MetaCanvasPreviewProps> = ({
  post,
  onUpdate,
  editorPlatform = "INSTAGRAM",
  uploadMedia,
  mediaUploadRef,
  carouselAddRef,
  renderUploadMenuPopover,
  toggleUploadMenu,
  onRemoveHeroMedia,
}) => {
  const format = getContentFormat(post);
  const slides = post.carousel_slides ?? [];
  const limits = CAROUSEL_LIMITS[editorPlatform];
  const formatOptions = getFormatOptions(editorPlatform);
  const carouselEmptyLabel =
    editorPlatform === "FACEBOOK"
      ? `Add photos (${limits.minPublish}+ for a Facebook carousel)`
      : `Add slides (${limits.minPublish}+ for Instagram carousel)`;
  const [activeIndex, setActiveIndex] = useState(0);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const formatMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (format !== "carousel") return;
    setActiveIndex((i) =>
      slides.length === 0 ? 0 : Math.min(i, Math.max(0, slides.length - 1))
    );
  }, [format, slides.length]);

  useEffect(() => {
    if (!formatMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (formatMenuRef.current && !formatMenuRef.current.contains(e.target as Node)) {
        setFormatMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [formatMenuOpen]);

  const handleFormatChange = (next: MetaContentFormat) => {
    setFormatMenuOpen(false);
    if (next === "carousel") {
      const seed =
        post.carousel_slides && post.carousel_slides.length > 0
          ? post.carousel_slides
          : post.image_url
            ? [{ url: post.image_url, type: "IMAGE" as const }]
            : [];
      onUpdate({
        ...post,
        content_format: "carousel",
        instagram_content_format: "carousel",
        carousel_slides: seed,
        image_url: seed[0]?.url ?? post.image_url,
        video_url: "",
      });
      setActiveIndex(0);
      return;
    }
    if (next === "video") {
      onUpdate({
        ...post,
        content_format: "video",
        instagram_content_format: "video",
        video_url: post.video_url || post.image_url || "",
        image_url: "",
        carousel_slides: undefined,
      });
      return;
    }
    onUpdate({
      ...post,
      content_format: "image",
      instagram_content_format: "image",
      image_url: post.image_url || post.carousel_slides?.[0]?.url || "",
      video_url: "",
      carousel_slides: undefined,
    });
  };

  const updateSlides = useCallback(
    (nextSlides: MetaCarouselSlide[]) => {
      onUpdate({
        ...post,
        content_format: "carousel",
        instagram_content_format: "carousel",
        carousel_slides: nextSlides,
        image_url: nextSlides[0]?.url ?? post.image_url,
      });
    },
    [onUpdate, post]
  );

  const removeCarouselSlideAt = useCallback(
    (index: number) => {
      if (format !== "carousel" || index < 0 || index >= slides.length) return;
      const next = slides.filter((_, i) => i !== index);
      onUpdate({
        ...post,
        content_format: "carousel",
        instagram_content_format: "carousel",
        carousel_slides: next.length > 0 ? next : [],
        image_url: next[0]?.url ?? "",
        video_url: "",
      });
      setActiveIndex((prev) => {
        if (next.length === 0) return 0;
        if (index < prev) return prev - 1;
        if (index === prev) return Math.min(prev, next.length - 1);
        return prev;
      });
    },
    [format, slides, onUpdate, post]
  );
  const currentFormatMeta = formatOptions.find((o) => o.value === format) ?? formatOptions[1];

  const renderEmpty = (label: string, icon: string, placement: "media" | "carouselAdd" = "media") => (
    <button
      type="button"
      onClick={() => toggleUploadMenu(placement)}
      className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-lg bg-theme-background/30"
    >
      <span className="material-icons text-4xl text-theme-fg-muted">{icon}</span>
      <span className="px-4 text-center text-[13px] font-semibold text-theme-fg-muted">
        {label}
      </span>
    </button>
  );

  return (
    <div className="flex w-full flex-col">
      <div className="border-b border-theme-border/50 px-4 pt-3 pb-3">
        <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">
          Content format
        </label>
        <div className="relative" ref={formatMenuRef}>
          <button
            type="button"
            onClick={() => setFormatMenuOpen((o) => !o)}
            className="flex w-full items-center gap-3 rounded-xl border border-theme-border bg-theme-surface px-3 py-2.5 text-left shadow-sm outline-none ring-theme-primary/30 transition-colors hover:border-theme-border hover:bg-theme-background/80 focus:border-theme-primary focus:ring-2"
            aria-expanded={formatMenuOpen}
            aria-haspopup="listbox"
          >
            <FormatIcon name={currentFormatMeta.icon} className="text-[22px] text-theme-fg-muted" />
            <span className="flex-1 text-[14px] font-semibold text-theme-fg">
              {currentFormatMeta.label}
            </span>
            <span className="material-icons text-theme-fg-muted">
              {formatMenuOpen ? "expand_less" : "expand_more"}
            </span>
          </button>
          {formatMenuOpen && (
            <ul
              role="listbox"
              className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-xl border border-theme-border bg-theme-surface py-1 shadow-lg"
            >
              {formatOptions.map((opt) => (
                <li key={opt.value} role="option" aria-selected={format === opt.value}>
                  <button
                    type="button"
                    onClick={() => handleFormatChange(opt.value)}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-[14px] font-medium transition-colors hover:bg-theme-secondary/15 ${
                      format === opt.value
                        ? "bg-theme-primary/10 text-theme-primary"
                        : "text-theme-fg"
                    }`}
                  >
                    <FormatIcon
                      name={opt.icon}
                      className={`text-[22px] ${format === opt.value ? "text-theme-primary" : "text-theme-fg-muted"}`}
                    />
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div
        ref={mediaUploadRef as React.Ref<HTMLDivElement> | undefined}
        className="relative w-full overflow-hidden rounded-b-none bg-theme-background"
      >
        {renderUploadMenuPopover("media")}

        {format === "image" &&
          (post.image_url ? (
            <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
              <img
                src={post.image_url}
                alt=""
                className="h-full w-full object-contain"
              />
              {onRemoveHeroMedia && (
                <button
                  type="button"
                  onClick={onRemoveHeroMedia}
                  className="absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-theme-danger text-white shadow-lg"
                  title="Remove"
                >
                  <span className="material-icons text-lg">close</span>
                </button>
              )}
            </div>
          ) : (
            renderEmpty("Add image", "add_photo_alternate")
          ))}

        {format === "video" &&
          (post.video_url ? (
            <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
              <video
                src={post.video_url}
                controls
                className="h-full w-full object-contain"
              />
              {onRemoveHeroMedia && (
                <button
                  type="button"
                  onClick={onRemoveHeroMedia}
                  className="absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-theme-danger text-white shadow-lg"
                  title="Remove"
                >
                  <span className="material-icons text-lg">close</span>
                </button>
              )}
            </div>
          ) : (
            renderEmpty("Add video", "videocam")
          ))}

        {format === "carousel" &&
          (slides.length === 0 ? (
            <div ref={carouselAddRef as React.Ref<HTMLDivElement>} className="relative w-full">
              {renderUploadMenuPopover("carouselAdd")}
              {renderEmpty(carouselEmptyLabel, "view_carousel", "carouselAdd")}
            </div>
          ) : (
            <CarouselSlideStrip
              slides={slides}
              maxSlides={limits.max}
              minPublishSlides={limits.minPublish}
              platformLabel={editorPlatform === "FACEBOOK" ? "Facebook" : "Instagram"}
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
              onRemoveSlide={removeCarouselSlideAt}
              onRemoveHero={() => removeCarouselSlideAt(activeIndex)}
              onReorder={(from, to) => updateSlides(reorderSlides(slides, from, to))}
              onAddSlide={uploadMedia ? () => toggleUploadMenu("carouselAdd") : undefined}
              addSlideContent={
                uploadMedia && slides.length < limits.max ? (
                  <div
                    ref={carouselAddRef as React.Ref<HTMLDivElement> | undefined}
                    className="relative z-20 shrink-0"
                  >
                    {renderUploadMenuPopover("carouselAdd")}
                    <button
                      type="button"
                      onClick={() => toggleUploadMenu("carouselAdd")}
                      className="flex h-20 w-20 items-center justify-center rounded-xl border-2 border-dashed border-theme-border bg-theme-surface text-theme-fg-muted transition-all hover:border-theme-primary hover:bg-theme-primary/5 hover:text-theme-primary"
                      title="Add slide"
                    >
                      <span className="material-icons">add</span>
                    </button>
                  </div>
                ) : undefined
              }
            />
          ))}
      </div>

      <div className="px-4 pt-4 pb-5">
        {post.title && (
          <h4 className="mb-3 text-[15px] font-bold leading-tight tracking-tight text-theme-fg">
            {post.title}
          </h4>
        )}
        <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">
          Caption
        </label>
        <textarea
          value={post.content}
          onChange={(e) => onUpdate({ ...post, content: e.target.value })}
          placeholder="Write a caption..."
          rows={4}
          className="w-full resize-y rounded-xl border border-theme-border bg-theme-surface px-3 py-2.5 text-[14px] leading-relaxed text-theme-fg placeholder:text-slate-400 outline-none transition-colors focus:border-theme-primary focus:ring-2 focus:ring-theme-primary/20"
        />
        {post.hashtags && post.hashtags.length > 0 && (
          <p className="mt-2 text-[13px] font-medium text-theme-fg-muted">
            {post.hashtags.map((tag) => (tag.startsWith("#") ? tag : `#${tag}`)).join(" ")}
          </p>
        )}
      </div>
    </div>
  );
};

/** @deprecated Use MetaCanvasPreview with editorPlatform="INSTAGRAM" (default). */
export const InstagramCanvasPreview = MetaCanvasPreview;
